import { supabase } from '../../shared/supabase/supabaseClient';
import {
  clearSessionEmployeeCache,
  resolveSessionEmployee,
} from '../../auth/application/sessionEmployeeResolver';
import {
  clearBootstrapSessionCache,
  peekBootstrapSessionCache,
} from './bootstrapMobileSessionService';
import { awaitSessionBootstrap, clearSessionBootstrapGate } from './hydrateSessionCachesFromBootstrap';
import { canRunOperationalBackgroundWork } from './operationalBackgroundWork';
import { EMPLOYEE_PERMISSIONS_FRESHNESS_POLL_MS } from './sessionCacheTtl';
import { getSessionOrganizationId } from './sessionOrganization';
import { useSessionStore } from './store/useSessionStore';
import { bumpEmployeePermissionsFreshness } from './employeePermissionsFreshnessEvents';
import {
  shouldRefreshEmployeePermissions,
  type EmployeeAccessStamp,
} from './employeePermissionsFreshness';

/**
 * Lectura liviana de stamp de acceso del empleado (incluye inactivo).
 * RLS: self-select por auth_user_id.
 */
export async function fetchEmployeeAccessStamp(userId: string): Promise<EmployeeAccessStamp | null> {
  const { data: rows, error } = await supabase
    .from('employees')
    .select('id, is_active, organization_id, permissions_updated_at, role')
    .eq('auth_user_id', userId)
    .limit(10);

  if (error || !rows?.length) return null;

  // role='owner' = membresía del dueño, no empleo — sin stamp de permisos.
  const list = (rows as Array<{
    id: string;
    is_active: boolean | null;
    organization_id: string | null;
    permissions_updated_at: string | null;
    role: string | null;
  }>).filter((r) => r.role !== 'owner');

  let orgId: string | null = null;
  try {
    orgId = await getSessionOrganizationId();
  } catch {
    orgId = null;
  }

  const orgRows = orgId ? list.filter((r) => r.organization_id === orgId) : list;
  const pool = orgRows.length > 0 ? orgRows : list;
  const preferred =
    pool.find((r) => r.is_active !== false) ?? pool[0] ?? null;
  if (!preferred) return null;

  return {
    employeeId: preferred.id,
    isActive: preferred.is_active !== false,
    permissionsUpdatedAt:
      typeof preferred.permissions_updated_at === 'string' ? preferred.permissions_updated_at : null,
  };
}

/**
 * Compara stamp remoto vs sesión cacheada; si cambió, force re-bootstrap (best-effort).
 * Post-effect: no reporta fallo fatal al usuario (ver server-authoritative §4.1).
 */
export async function checkAndRefreshEmployeePermissionsFreshness(
  userId: string
): Promise<'skipped' | 'ok' | 'refreshed'> {
  if (!userId || !canRunOperationalBackgroundWork()) return 'skipped';

  let previous = null as Awaited<ReturnType<typeof resolveSessionEmployee>> | null;
  try {
    previous = await resolveSessionEmployee(userId);
  } catch {
    previous = null;
  }

  // Dueños / guests sin fila employee: no pollear.
  if (previous && previous.isEmployee !== true && previous.isGrant === true) {
    return 'skipped';
  }

  // Si el bootstrap acaba de correr (< intervalo de poll) su sección `employee`
  // ya trae el stamp fresco — evita el REST suelto en el primer tick del boot.
  // Pasada esa ventana el poll va a la base (detecta revocaciones post-boot).
  const freshBootstrap = peekBootstrapSessionCache(userId, EMPLOYEE_PERMISSIONS_FRESHNESS_POLL_MS);

  let next: EmployeeAccessStamp | null = null;
  if (freshBootstrap) {
    const emp = freshBootstrap.employee;
    next =
      emp.isEmployee && emp.employeeId
        ? {
            employeeId: emp.employeeId,
            isActive: emp.isActive,
            permissionsUpdatedAt: emp.permissionsUpdatedAt ?? null,
          }
        : null;
  } else {
    try {
      next = await fetchEmployeeAccessStamp(userId);
    } catch (e) {
      console.warn('[employeePermissionsFreshness] stamp fetch failed', e);
      return 'ok';
    }
  }

  // Sin fila: si antes éramos empleado, forzar refresh (edge raro).
  if (!next) {
    if (previous?.isEmployee === true) {
      return forceSessionEmployeeRefresh(userId);
    }
    return 'skipped';
  }

  if (!shouldRefreshEmployeePermissions({ previous, next })) {
    return 'ok';
  }

  return forceSessionEmployeeRefresh(userId);
}

async function forceSessionEmployeeRefresh(userId: string): Promise<'refreshed'> {
  clearSessionEmployeeCache();
  clearBootstrapSessionCache();
  clearSessionBootstrapGate();

  try {
    const payload = await awaitSessionBootstrap(userId, { force: true });
    if (payload?.branches) {
      useSessionStore.getState().setBranches(payload.branches);
    }
  } catch (e) {
    console.warn('[employeePermissionsFreshness] bootstrap refresh failed', e);
    // Fallback: al menos re-resolve employee para soft-block.
    try {
      await resolveSessionEmployee(userId, { force: true });
    } catch {
      /* ignore */
    }
  }

  bumpEmployeePermissionsFreshness();
  return 'refreshed';
}
